import { CONTRACT_STATUS } from '@/config/statuses';

// Every contract action, defined once: the list's row + menu actions and the detail header's.

const isClosed = (row) => row?.statusCode === CONTRACT_STATUS.closed;
const hasLinkedShipments = (row) => Number(row?.totalShipmentLinked) > 0;

export const CONTRACT_ACTIONS = Object.freeze({
  // A closed contract takes no more shipments, links, edits or files.
  receiveShipment: { disabled: isClosed },
  linkShipment: { disabled: isClosed },
  addAttachment: { disabled: isClosed },
  edit: { disabled: isClosed },
  markClosed: { hidden: isClosed },
  unmarkClosed: { hidden: (row) => !isClosed(row) },
  // The server rejects both cases, so the menu item can't offer them.
  delete: { disabled: (row) => isClosed(row) || hasLinkedShipments(row) },
});

// The status the toggle route should be sent for this row.
export const nextContractStatus = (row) =>
  isClosed(row) ? CONTRACT_STATUS.active : CONTRACT_STATUS.closed;

/**
 * Handlers, each (row) => void:
 *   onReceiveShipment · onLinkShipment · onAddAttachment · onToggleClosed · onEdit · onDelete
 */
export const buildContractActions = (t, handlers) => ({
  receiveShipment: {
    id: 'receive-shipment',
    label: t('ACTION_RECEIVE_SHIPMENT'),
    disabled: CONTRACT_ACTIONS.receiveShipment.disabled,
    onClick: handlers.onReceiveShipment,
  },
  linkShipment: {
    id: 'link-shipment',
    label: t('ACTION_LINK_SHIPMENT'),
    disabled: CONTRACT_ACTIONS.linkShipment.disabled,
    onClick: handlers.onLinkShipment,
  },
  addAttachment: {
    id: 'add-attachment',
    label: t('ACTION_ADD_ATTACHMENT'),
    disabled: CONTRACT_ACTIONS.addAttachment.disabled,
    onClick: handlers.onAddAttachment,
  },
  markClosed: {
    id: 'mark-closed',
    label: t('CONTRACTS_ACTION_MARK_CLOSED'),
    hidden: CONTRACT_ACTIONS.markClosed.hidden,
    onClick: handlers.onToggleClosed,
  },
  unmarkClosed: {
    id: 'unmark-closed',
    label: t('CONTRACTS_ACTION_UNMARK_CLOSED'),
    hidden: CONTRACT_ACTIONS.unmarkClosed.hidden,
    onClick: handlers.onToggleClosed,
  },
  edit: {
    id: 'edit',
    label: t('COMMON_EDIT'),
    disabled: CONTRACT_ACTIONS.edit.disabled,
    onClick: handlers.onEdit,
  },
  delete: {
    id: 'delete',
    label: t('COMMON_DELETE'),
    disabled: CONTRACT_ACTIONS.delete.disabled,
    onClick: handlers.onDelete,
  },
});

// The two the list draws inline and the detail header draws as buttons — in that order.
export const contractRowActions = (actions) => [actions.receiveShipment, actions.linkShipment];

// The rest, in the order both "⋯" menus draw them.
export const contractMenuActions = (actions) => [
  actions.addAttachment,
  actions.markClosed,
  actions.unmarkClosed,
  actions.edit,
  actions.delete,
];

// One action against one row, for a surface that isn't <Table> — it resolves its own.
export const resolveContractAction = (action, row) => ({
  id: action.id,
  label: action.label,
  hidden: Boolean(action.hidden?.(row)),
  disabled: Boolean(action.disabled?.(row)),
  onSelect: () => action.onClick?.(row),
});

export const resolveContractMenuItems = (actions, row) =>
  contractMenuActions(actions)
    .map((action) => resolveContractAction(action, row))
    .filter((item) => !item.hidden);
